import { useEffect, useState, useCallback } from "react";
import { g, eur } from "@/lib/api";
import { usePlan } from "@/context/PlanContext";
import { useI18n } from "@/i18n";
import { PremiumLock } from "@/components/PremiumLock";
import { Card } from "@/components/ui/card";
import { Trash2, TrendingDown } from "lucide-react";

export default function Sprechi() {
  const { has } = usePlan();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    if (!has("waste")) return;
    try {
      setData(await g("/waste", { days }));
    } catch (e) {
      /* piano non abilitato */
    }
  }, [has, days]);
  useEffect(() => {
    load();
  }, [load]);

  if (!has("waste"))
    return (
      <PremiumLock
        title={t("x.waste.title")}
        description={t("x.waste.rows")}
        needed="standard"
        message={t("x.waste.title")}
        preview={
          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
            {[[t("x.waste.total"), "84,50 €"], [t("x.waste.dishes"), "11"]].map(([l, v]) => (
              <Card key={l} className="p-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
                <p className="font-num mt-3 text-2xl font-bold">{v}</p>
              </Card>
            ))}
          </div>
        }
      />
    );

  return (
    <div className="fade-up space-y-5" data-testid="sprechi-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("x.waste.overline")}</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("x.waste.title")}</h1>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`waste-days-${d}`}
              onClick={() => setDays(d)}
              className={`rounded-md px-3 py-2 text-xs font-semibold transition-colors ${days === d ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
            >
              {t("x.waste.days", { n: d })}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {[[t("x.waste.total"), eur(data?.total), TrendingDown], [t("x.waste.dishes"), data?.dishes ?? 0, Trash2]].map(([l, v, Icon]) => (
          <Card key={l} className="p-5" data-testid={`waste-kpi-${l.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-start justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
              <Icon className="h-4 w-4 text-slate-400" />
            </div>
            <p className="font-num mt-3 text-2xl font-bold">{v}</p>
          </Card>
        ))}
      </div>

      <Card className="p-5" data-testid="waste-per-reason">
        <h3 className="text-lg font-semibold">{t("x.waste.perReason")}</h3>
        {(data?.per_reason || []).length === 0 && <p className="mt-2 text-sm text-slate-500">{t("x.waste.none")}</p>}
        <div className="mt-3 space-y-2 text-sm">
          {(data?.per_reason || []).map((r) => (
            <div key={r.reason} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0" data-testid={`reason-${r.reason.replace(/\s+/g, "-")}`}>
              <span className="capitalize">{r.reason} <span className="text-[11px] text-slate-500">({r.count})</span></span>
              <span className="font-num font-bold">{eur(r.amount)}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5" data-testid="waste-rows">
        <h3 className="text-lg font-semibold">{t("x.waste.rows")}</h3>
        {(data?.rows || []).length === 0 && <p className="mt-2 text-sm text-slate-500">{t("x.waste.none")}</p>}
        <div className="mt-3 divide-y divide-slate-100">
          {(data?.rows || []).map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`waste-row-${r.id}`}>
              <div className="min-w-0">
                <p className="truncate font-medium">
                  <span className="font-num">{r.qty}×</span> {r.name}
                </p>
                <p className="text-[11px] text-slate-500">
                  {r.table} · {r.department} · {r.reason} · {r.by} ·{" "}
                  {new Date(r.at).toLocaleString(lang, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-num font-bold text-rose-600">-{eur(r.amount)}</p>
                <p className="text-[10px] text-slate-500">{r.prepared ? t("x.waste.prepared") : t("x.waste.notPrepared")}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
